// The value ↔ rail-position math behind `SignedSlider`: a piecewise-linear scale around an origin,
// so each side of it can keep its own linear scale (and a guaranteed share of the rail). Pure and
// React-free, so the slider stays a thin view and the scale can be unit-tested on its own.

// The underlying MUI slider runs on rail POSITIONS (0…RESOLUTION), not values, so each side of the
// origin can have its own linear scale.
export const RESOLUTION = 10_000;
// Absorbs float error before flooring/ceiling a value onto the step grid (−0.03 / 0.01 = −2.9999…).
const STEP_GRID_EPSILON = 1e-9;

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// Decimal places in a step (0.01 → 2, 0.25 → 2, 5 → 0), so a snapped value can drop the float noise
// that `Math.round(v / step) * step` leaves behind (55.620000000000005 → 55.62).
const decimalsOf = (step: number): number => {
	const fraction = step.toString().split(".")[1];
	return fraction === undefined ? 0 : fraction.length;
};

export type SignedScale = {
	min: number;
	max: number;
	step: number;
	origin: number;
	// Fraction of the rail (0…1) left of the origin.
	originShare: number;
};

// Where the origin sits on the rail. Without `minSideShare` that is its natural, proportional spot;
// with it each side keeps at least that share whenever the range spans the origin.
export const originShareFor = (min: number, max: number, origin: number, minSideShare: number | undefined): number => {
	const span = max - min;
	const naturalShare = span > 0 ? (clamp(origin, min, max) - min) / span : 0;
	const spansOrigin = min < origin && origin < max;
	// DEFAULT: no minimum share — one linear scale across the rail, the plain slider behaviour.
	return spansOrigin && minSideShare !== undefined ? clamp(naturalShare, minSideShare, 1 - minSideShare) : naturalShare;
};

// value → rail position (0…1), piecewise linear around the origin.
export const toPosition = ({ min, max, origin, originShare }: SignedScale, v: number): number => {
	const c = clamp(v, min, max);
	if (c < origin) return origin > min ? ((c - min) / (origin - min)) * originShare : 0;
	return max > origin ? originShare + ((c - origin) / (max - origin)) * (1 - originShare) : originShare;
};

// rail position (0…1) → value, the inverse of `toPosition`.
export const fromPosition = ({ min, max, origin, originShare }: SignedScale, p: number): number => {
	if (p < originShare) return min + (p / originShare) * (origin - min);
	return 1 - originShare > 0 ? origin + ((p - originShare) / (1 - originShare)) * (max - origin) : origin;
};

// Integer rail position (0…RESOLUTION) → reported value. Exact bounds and origin pass through
// untouched, so a bound is always reachable; anything else snaps to `step`.
export const valueAtPosition = (scale: SignedScale, position: number): number => {
	if (position <= 0) return scale.min;
	if (position >= RESOLUTION) return scale.max;
	if (position === Math.round(scale.originShare * RESOLUTION)) return scale.origin;
	const snapped = Number((Math.round(fromPosition(scale, position / RESOLUTION) / scale.step) * scale.step).toFixed(decimalsOf(scale.step)));
	return clamp(snapped, scale.min, scale.max);
};

// The value `stepsToMove` VALUE steps away from `value` (negative = toward `min`). Off-grid values (a
// backend-suggested −2.7345) move to the next grid line in that direction, never stay put.
export const stepValue = ({ min, max, step }: SignedScale, value: number, stepsToMove: number): number => {
	const onGrid = value / step;
	const target = stepsToMove > 0 ? Math.floor(onGrid + STEP_GRID_EPSILON) + stepsToMove : Math.ceil(onGrid - STEP_GRID_EPSILON) + stepsToMove;
	return clamp(Number((target * step).toFixed(decimalsOf(step))), min, max);
};
